import { ChangeDetectionStrategy, Component } from '@angular/core';
import * as cee from '../../Common/CeeEnvisionWebComponents';
import * as helpers from '../../helpers';
import { C3Service } from '../../c3.service';
import { FeatureEditorChild } from '../feature-editor/feature-editor.component';

@Component({
    selector: 'app-cutting-plane-editor',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './cutting-plane-editor.component.html',
    styleUrls: ['./cutting-plane-editor.component.css'],
    standalone: false
})
export class CuttingPlaneEditorComponent extends FeatureEditorChild {
  private _c3Service: C3Service;
  private _cuttingPlane!: cee.ug.CuttingPlane;
  private _relativePosition!: number;
  private _pickActive = false;

  public get scalarResults() {
    return this._c3Service.scalarResultsWithNone;
  }
  public get vectorResults() {
    return this._c3Service.vectorResultsWithNone;
  }
  public get hasCuttingPlane() {
    return this._cuttingPlane !== undefined;
  }
  public get relativePosition() {
    return this._relativePosition;
  }
  public get normal() {
    return this._cuttingPlane.normal;
  }
  public get clipping() {
    return this._cuttingPlane.clipping;
  }
  public get mapScalarResultId() {
    return this._cuttingPlane.mapScalarResultId;
  }
  public get mapVectorResultId() {
    return this._cuttingPlane.mapVectorResultId;
  }
  public get vectorGridSpacing() {
    return this._cuttingPlane.vectorGridSpacing;
  }
  public get opacity() {
    return this._cuttingPlane.opacity;
  }
  public get showVectorGridSpacing() {
    return this.mapVectorResultId > -1;
  }

  public get positionString() {
    const numDps = 6;
    return helpers.vec3ToString(this._cuttingPlane.point, numDps);
  }

  public get normalString() {
    const numDps = 6;
    return helpers.vec3ToString(this._cuttingPlane.normal, numDps);
  }

  public get pickButtonClass() {
    return this._pickActive ? 'active' : '';
  }

  public constructor(c3Service: C3Service) {
    super();
    this._c3Service = c3Service;
  }

  public getCuttingPlanes = () => {
    return this._c3Service._model['0']
      .getCuttingPlaneArray()
      .map((cp: { id: 0 }) => ({ id: cp.id, name: 'Cutting plane ' + cp.id }));
  };

  public addCuttingPlane = () => {
    const cuttingPlane = this._c3Service._model['0'].addCuttingPlane();
    const scalarResults = this._c3Service._model['0'].modelDirectory.scalarResultArray;

    if (scalarResults.length > 0) {
      cuttingPlane.mapScalarResultId = scalarResults[0].id;
    }

    return cuttingPlane.id;
  };

  public deleteCuttingPlane = (id: number) => {
    this._c3Service._model['0'].deleteCuttingPlaneById(id);
  };

  public cuttingPlaneIdChanged(id: number) {
    this._cuttingPlane = id !== undefined ? this._c3Service._model['0'].getCuttingPlaneById(id) : undefined;

    if (this._cuttingPlane) {
      this._relativePosition = cee.ug.CuttingPlaneUtils.getRelativeNormalPosition(
        this._cuttingPlane.point,
        this._cuttingPlane.normal,
        this._c3Service._model['0'].getBoundingBox()
      );
    }
  }

  public relativePositionChanged(relPosSliderValue: string) {
    const value = parseFloat(relPosSliderValue) / 1000;
    this._cuttingPlane.point = cee.ug.CuttingPlaneUtils.getPositionFromRelativeNormalPosition(
      value,
      this._cuttingPlane.normal,
      this._c3Service._model['0'].getBoundingBox()
    );
  }

  public positionStringChanged(value: string) {
    const position = helpers.stringToVec3(value);
    if (position) {
      this._cuttingPlane.point = position;
    }
  }

  public normalChanged(x: number, y: number, z: number) {
    this._cuttingPlane.normal = { x, y, z };
  }

  public normalStringChanged(value: string) {
    const normal = helpers.stringToVec3(value);
    if (normal) {
      this._cuttingPlane.normal = normal;
    }
  }

  public clippingChanged(value: boolean) {
    this._cuttingPlane.clipping = value;
  }

  public mapScalarResultIdChanged(value: string) {
    this._cuttingPlane.mapScalarResultId = parseInt(value);
  }

  public mapVectorResultIdChanged(value: string) {
    this._cuttingPlane.mapVectorResultId = parseInt(value);
  }

  public vectorGridSpacingChanged(value: number) {
    this._cuttingPlane.vectorGridSpacing = value;
  }

  public opacityChanged(value: number) {
    this._cuttingPlane.opacity = value;
  }

  public togglePick() {
    this._pickActive = !this._pickActive;

    if (!this._pickActive) {
      this._c3Service.removePick();
    } else {
      this._c3Service.performPick((hitItem) => {
        if (hitItem.anythingHit) {
          this._cuttingPlane.point = hitItem.intersectionPoint;
          this._cuttingPlane.normal = hitItem.intersectionPointNormal;
          this._pickActive = false;
        }
      });
    }
  }
}
